<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon, IconTypes } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import { Group, Theme, UserEntity } from '@/types';
import { formatDate } from '@/utils/formatters';

interface Props {
  theme: Theme;
  memberCount: number;
  creator?: UserEntity;
  group?: Group;
  iconName: IconTypes;
}

const props = withDefaults(defineProps<Props>(), {
  creator: undefined,
});

const isIconFilled = computed(() => props.iconName === 'star-fill');

type Emits = {
  click: [];
};

const emit = defineEmits<Emits>();

const onClick = () => {
  emit('click');
};

const metaText = computed(() =>
  [
    props.group?.name,
    formatDate(props.theme.created_at),
    `${props.memberCount}人`,
  ]
    .filter(s => s && s.length > 0)
    .join('・'),
);

const isNewTheme = (theme: Theme) =>
  dayjs().subtract(3, 'day') < dayjs(theme.created_at);
</script>

<template>
  <div class="favorite-theme-select-item" @click="onClick">
    <DgrIcon
      :name="iconName"
      :keep-fill="false"
      class="favorite-icon"
      :class="{ filled: isIconFilled }"
    />
    <div class="theme-info">
      <span class="c-title-bold--m theme-name"
        >{{ theme.name
        }}<span v-if="isNewTheme(theme)" class="c-text-regular--xs new-tag"
          >新着</span
        ></span
      >
      <span class="c-text-regular--s description">{{ theme.description }}</span>
      <div class="c-text-regular--s meta">
        <template v-if="creator">
          <img :src="creator.image_url" class="profile-image" />
          <span class="creator">{{ creator.user_name }}・</span>
        </template>
        <span>{{ metaText }}</span>
        <span v-if="theme.is_member" class="theme-member"
          >・追加済み
          <DgrIcon
            name="info"
            size="small"
            :keep-fill="false"
            v-tooltip="{
              content:
                'ワークスペースに追加済みのテーマです。お気に入りに登録すると、毎日おすすめ情報が届きます。',
              theme: 'small',
            }"
        /></span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.favorite-theme-select-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 16px;
  cursor: pointer;

  &:hover {
    background: $color-gray200;

    .favorite-icon {
      color: $color-yl7;
    }
  }

  &:not(:last-child) {
    border-bottom: 1px solid $color-gray400;
  }

  .favorite-icon {
    cursor: pointer;

    &.filled {
      color: $color-yl7;
    }
  }

  .theme-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    .theme-name {
      display: flex;
      align-items: center;

      .new-tag {
        display: inline-flex;
        padding: 0px 8px;
        height: 16px;
        color: $color-orange1000;
      }
    }

    .description {
      color: $color-gray800;
    }

    .meta {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      color: $color-gray800;

      .profile-image {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        margin-right: 4px;
      }

      .theme-member {
        display: flex;
        align-items: center;
        .icon-box {
          display: inline-flex;
        }
      }
    }
  }
}
</style>
